/**
 * User Sync Service
 * Handles user document creation and updates on signup/login
 */

import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../firebase';
import { invalidateUserCache } from '../userService';

const USERS_COLLECTION = 'users';

/**
 * Default values for new users
 */
const DEFAULT_USER_VALUES = {
  role: 'user',
  plan: 'free',
};

/**
 * Admin email configuration
 * This email will be automatically promoted to admin with growth plan
 */
const ADMIN_EMAIL = 'ze_casal@hotmail.com';

/**
 * Detect authentication provider from Firebase Auth user
 * @param {Object} authUser - Firebase Auth user object
 * @returns {string} Provider name ('google' | 'password')
 */
export function detectProvider(authUser) {
  if (!authUser || !authUser.providerData || authUser.providerData.length === 0) {
    return 'password'; // Default fallback
  }
  
  const providerId = authUser.providerData[0].providerId;
  
  if (providerId === 'google.com') {
    return 'google';
  }
  
  // For email/password authentication, return 'email'
  return 'email';
}

/**
 * Check if email should be admin
 * @param {string} email - User email
 * @returns {boolean} True if email matches admin email
 */
function isAdminEmail(email) {
  if (!email) return false;
  return email.toLowerCase() === ADMIN_EMAIL.toLowerCase();
}

/**
 * Sync user document on signup or login
 * Creates document if it doesn't exist, updates lastLoginAt always
 * @param {Object} authUser - Firebase Auth user object
 * @returns {Promise<Object>} User document data
 */
export async function syncUserDocument(authUser) {
  if (!authUser || !authUser.uid) {
    throw new Error('Invalid user: uid is required');
  }

  if (!authUser.email) {
    throw new Error('Invalid user: email is required');
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, authUser.uid);
    const existingDoc = await getDoc(userRef);
    
    const provider = detectProvider(authUser);
    const isAdmin = isAdminEmail(authUser.email);
    
    if (existingDoc.exists()) {
      // User document exists - update lastLoginAt
      const updateData = {
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      // Update provider if it changed
      const existingData = existingDoc.data();
      if (existingData.provider !== provider) {
        updateData.provider = provider;
      }
      
      // Auto-promote admin email if not already admin
      if (isAdmin && existingData.role !== 'admin') {
        console.log(`­ƒöÉ Auto-promoting ${authUser.email} to admin`);
        updateData.role = 'admin';
        updateData.plan = 'growth'; // Admin always has growth plan
      }
      
      await updateDoc(userRef, updateData);
      
      // Invalidate cache after update
      invalidateUserCache(authUser.uid);
      
      console.log(`Ô£à User document updated for uid: ${authUser.uid}`);
      
      return {
        ...existingData,
        ...updateData,
        uid: authUser.uid,
        email: authUser.email,
      };
    } else {
      // User document doesn't exist - create it
      // IMPORTANT: All new users default to 'free' plan with 3 video limit
      // Only admin email gets 'growth' plan automatically
      const userData = {
        uid: authUser.uid,
        email: authUser.email,
        provider: provider,
        role: isAdmin ? 'admin' : DEFAULT_USER_VALUES.role,
        plan: isAdmin ? 'growth' : DEFAULT_USER_VALUES.plan, // 'free' for all new users
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Optional fields
        displayName: authUser.displayName || null,
        // Stripe fields (will be set by webhooks)
        stripeCustomerId: null,
        stripeSubscriptionId: null,
        subscriptionStatus: null,
        subscriptionStartDate: null,
        subscriptionEndDate: null,
        subscriptionCancelAtPeriodEnd: false,
      };
      
      await setDoc(userRef, userData);
      
      // Cache the new user document
      // Note: We import invalidateUserCache but we can also directly cache it
      // Since it's a new document, we'll let the next getUserDocument call cache it
      
      if (isAdmin) {
        console.log(`­ƒöÉ Admin user created for ${authUser.email}`);
      } else {
        console.log(`Ô£à User document created for uid: ${authUser.uid}`);
      }
      
      return userData;
    }
  } catch (error) {
    console.error('Error syncing user document:', error);
    throw error;
  }
}

/**
 * Update user's last login timestamp
 * @param {string} uid - User ID
 * @returns {Promise<void>}
 */
export async function updateLastLogin(uid) {
  if (!uid) {
    throw new Error('User ID is required');
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid);
    await updateDoc(userRef, {
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating last login:', error);
    throw error;
  }
}

